import {
  Box,
  HStack,
  Stack,
  Text,
  VStack,
  Image,
  IconButton,
  Button,
  Skeleton,
  Tag,
  TagLeftIcon,
  TagLabel,
  useDisclosure,
  // Alert,
  // AlertIcon,
} from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";

import { ReactComponent as VirtualCardLogo } from "assets/svg/atm-logo.svg";
import { ReactComponent as VirtualCardChip } from "assets/svg/virtual-chip.svg";
import { ReactComponent as MasterCardIcon } from "assets/svg/icons-new/master.svg";
import { ReactComponent as VisaCardIcon } from "assets/svg/icons-new/visa.svg";

import CustomButton from "components/Button/Button";
import Confirmation from "components/Modals/p2p/Confirmation";
import { BsShieldLockFill } from "react-icons/bs";

import map from "assets/png/virtual-card-bg.png";

import chunk from "lodash/chunk";
import toUpper from "lodash/toUpper";
import formatter from "util/formatter";
import { useMemo } from "react";

export const getCardScheme = (type) => {
  const map = {
    tier1: "whitesmoke",
    tier2: "red",
    tier3: "blue",
    tier4: "green",
    tier5: "black",
  };

  return map[type ?? "tier1"];
};

const Card = ({
  colorScheme,
  limit,
  validity,
  number,
  name,
  balance,
  currency,
  showBalance,
  onClick,
  isFrozen,
  isLoading,
  isUnfreezing,
  onUnfreeze,
  mask,
  brand,
  status,
  showStatus,
  ...xprops
}) => {
  const { isOpen, onClose, onOpen } = useDisclosure();

  const getColorScheme = () => {
    const map = {
      white: {
        foreground: "black",
        background: "white",
        borderColor: "#8688942f",
      },
      whitesmoke: {
        foreground: "black",
        background: "whitesmoke",
        borderColor: "#8688942f",
      },
      red: {
        foreground: "white",
        background:
          "linear-gradient(156.39deg, #DA4040 6.76%, #9E2929 88.75%), #DA4040",
        borderColor: "transparent",
      },
      blue: {
        foreground: "white",
        background:
          "linear-gradient(156.39deg, #3D90FF 6.76%, #1D70E1 88.75%), #1D70E1",
        borderColor: "transparent",
      },
      green: {
        foreground: "white",
        background:
          "linear-gradient(156.39deg, #66C961 6.76%, #3F943B 88.75%), #79D775",
        borderColor: "transparent",
      },
      black: {
        foreground: "white",
        background:
          "linear-gradient(156.39deg, #282930 6.76%, #16171C 88.75%), #282930;",
        borderColor: "transparent",
      },
    };

    return map[colorScheme ?? "whitesmoke"];
  };

  const handleClick = () => {
    onClick && onClick({ limit, colorScheme });
  };

  const handleUnfreeze = () => {
    onClose();
    onUnfreeze && onUnfreeze();
  };

  const cardNumber = useMemo(() => {
    if (!!number) return number;
    if (!number && !!mask) return String(mask).repeat(16);
    return "1234567890234567";
  }, [number, mask]);

  const BrandIcon = useMemo(() => {
    if (String(brand).toLowerCase().includes("visa")) return VisaCardIcon;
    if (String(brand).toLowerCase().includes("master")) return MasterCardIcon;
    return null;
  }, [brand]);

  const posb = useMemo(() => (BrandIcon ? "52px" : "0px"), [BrandIcon]);
  const posr = useMemo(() => (BrandIcon ? "60px" : "0px"), [BrandIcon]);
  const pos = useMemo(() => (BrandIcon ? "absolute" : "relative"), [BrandIcon]);

  return (
    <Box m="0 !important" w={["100%", "100%", "340px"]} {...xprops}>
      <Button
        w={["100%", "100%", "340px"]}
        h={["216px"]}
        border="1px solid transparent"
        borderColor={`${getColorScheme()?.borderColor} !important`}
        borderRadius="12px"
        p="24px 28px"
        // bgImage="url('/assets/png/virtual-card-bg.png')"
        pos="relative"
        overflow="hidden"
        bg={getColorScheme()?.background}
        variant="unstyled"
        _focus={{ shadow: "0 0 0 3px var(--focusColor)" }}
        onClick={isFrozen ? undefined : handleClick}
        cursor={isFrozen ? "auto" : "pointer"}
      >
        {isFrozen && (
          <Box
            pos="absolute"
            w="100%"
            h="100%"
            bg="#000000d1"
            zIndex="9"
            top="0"
            left="0"
          >
            <VStack
              h="100%"
              justifyContent="flex-end"
              alignItems="center"
              pos="relative"
            >
              <Tag
                size="md"
                variant="subtle"
                colorScheme="red"
                pos="absolute"
                right="20px"
                top="20px"
              >
                <TagLeftIcon boxSize="12px" as={BsShieldLockFill} />
                <TagLabel>Frozen</TagLabel>
              </Tag>
              <Box py="28px">
                <CustomButton
                  disabled={isUnfreezing ?? false}
                  isLoading={isUnfreezing ?? false}
                  onClick={onOpen}
                  bg="red.500"
                  _hover={{ shadow: "0 0 0 3px #f56565" }}
                  _active={{ shadow: "0 0 0 3px #f56565" }}
                  _focus={{ shadow: "0 0 0 3px #f56565" }}
                >
                  Unfreeze
                </CustomButton>
              </Box>
            </VStack>
          </Box>
        )}

        {status && showStatus && !isFrozen && (
          <Box
            pos="absolute"
            w="100%"
            h="100%"
            bg="#0000001f"
            zIndex="9"
            top="0"
            left="0"
          >
            <VStack
              h="100%"
              justifyContent="flex-end"
              alignItems="center"
              pos="relative"
            >
              <Tag
                size="sm"
                variant="subtle"
                colorScheme="blue"
                pos="absolute"
                right="5px"
                top="5px"
              >
                <TagLeftIcon boxSize="12px" as={BsShieldLockFill} />
                <TagLabel fontSize="12px" textTransform="uppercase">
                  {status}
                </TagLabel>
              </Tag>
            </VStack>
          </Box>
        )}

        <Image
          pointerEvents="none"
          src={map}
          pos="absolute"
          top="30%"
          left="0"
        />
        <HStack
          alignItems={showBalance ? "flex-start" : "center"}
          justifyContent="space-between"
          pos="relative"
          zIndex="2"
        >
          <Skeleton isLoaded={!isLoading}>
            <Box as={VirtualCardLogo} color={getColorScheme()?.foreground} />
          </Skeleton>
          {!balance && !showBalance && (
            <Skeleton isLoaded={!isLoading}>
              <Box as={VirtualCardChip} />
            </Skeleton>
          )}
          {(balance === 0 || balance > 0) && showBalance && (
            <VStack
              justifyContent="flex-end"
              alignItems="flex-end"
              color={getColorScheme()?.foreground}
            >
              <Skeleton
                mb={isLoading ? "4px !important" : "0 !important"}
                h={isLoading ? "8px" : "fit-content"}
                isLoaded={!isLoading}
              >
                <Text fontSize="xs" textShadow="0px 2px 2px rgba(0, 0, 0, 0.2)">
                  Balance
                </Text>
              </Skeleton>
              <Skeleton
                mt="0 !important"
                isLoaded={!isLoading}
                h={isLoading ? "12px" : "fit-content"}
              >
                <Text fontSize="sm" textShadow="0px 2px 2px rgba(0, 0, 0, 0.2)">
                  {((balance === 0 || balance > 0) &&
                    currency &&
                    `${formatter(currency).format(balance)}`) ??
                    "0"}
                </Text>
              </Skeleton>
            </VStack>
          )}
        </HStack>

        <Stack
          mt="40px"
          pos="relative"
          zIndex="2"
          color={getColorScheme()?.foreground}
        >
          <HStack
            fontFamily="'Credit Card', sans-serif"
            justifyContent="space-between"
            w="100%"
          >
            {chunk(cardNumber, 4).map((cc) => (
              <Skeleton
                mt="0 !important"
                isLoaded={!isLoading}
                fontFamily="inherit"
              >
                <Text
                  fontFamily="inherit"
                  m="0 !important"
                  textShadow="0px 2px 2px rgba(0, 0, 0, 0.2)"
                >
                  {cc}
                </Text>
              </Skeleton>
            ))}
          </HStack>

          <HStack
            justifyContent="space-between"
            mt="24px !important"
            textShadow="0px 0px 1px rgba(0, 0, 0, 0.5)"
          >
            <Skeleton
              mt="0 !important"
              isLoaded={!isLoading}
              h={isLoading ? "12px" : "fit-content"}
            >
              <Text fontSize="10px" fontWeight="500">
                {toUpper(name ?? "YOUR NAME HERE")}
              </Text>{" "}
            </Skeleton>

            <HStack pos="relative">
              <HStack pos={pos} bottom={posb} right={posr}>
                <Stack alignSelf="flex-end">
                  <Text fontSize="7px" fontWeight="500">
                    VALID
                  </Text>
                  <Text fontSize="7px" fontWeight="500" m="0 !important">
                    THRU
                  </Text>
                </Stack>

                <VStack alignSelf="flex-end">
                  <Skeleton
                    mb={isLoading ? "2px !important" : "0 !important"}
                    isLoaded={!isLoading}
                    h={isLoading ? "8px" : "fit-content"}
                  >
                    <Text fontSize="6px" fontWeight="500">
                      MONTH/YEAR
                    </Text>
                  </Skeleton>
                  <Skeleton
                    mt="0 !important"
                    isLoaded={!isLoading}
                    h={isLoading ? "12px" : "fit-content"}
                  >
                    <Text
                      fontSize="sm"
                      fontWeight="600"
                      m="0 !important"
                      lineHeight="16px"
                    >
                      {validity ?? "00/00"}
                    </Text>
                  </Skeleton>
                </VStack>
              </HStack>

              {BrandIcon && <Box as={BrandIcon} w="40px" />}
            </HStack>
          </HStack>
        </Stack>
      </Button>

      {/* {status && showStatus && (
        <Box mt="10px">
          <Alert status="info" borderRadius="8px">
            <AlertIcon />
            Your card is now {status}
          </Alert>
        </Box>
      )} */}

      {limit && (
        <Stack py="16px" borderBottom="1px solid  #D9DBE94f">
          <Text
            color="#595B67"
            lineHeight="22px"
            fontWeight="500"
            fontSize="13px"
          >
            Transaction limit
          </Text>
          <HStack mt="0 !important" justifyContent="space-between">
            <Text color="#868894" fontSize="13px" fontWeight="500">
              {formatter("usd").format(limit)} per month per card
            </Text>
            <IconButton
              aria-label="right arrow"
              size="sm"
              borderRadius="50%"
              bg="green.400"
              _hover={{ bg: "green.600" }}
              _active={{ bg: "green.600" }}
              _focus={{
                bg: "green.600",
                shadow: "0 0 0 3px var(--focusColor)",
              }}
              icon={<Box color="white" fontSize="md" as={ChevronRightIcon} />}
              onClick={handleClick}
            />
          </HStack>
        </Stack>
      )}

      <Confirmation
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleUnfreeze}
        title="Confirm Card Unfreeze"
      >
        <VStack>
          <Text>Are you sure you want to unfreeze this card?</Text>
        </VStack>
      </Confirmation>
    </Box>
  );
};

export default Card;
